import { CircleHelp, X } from 'lucide-react'
import { forwardRef, useEffect, useRef, useState, type ReactNode } from 'react'

import { cn } from '@/lib/utils'

// --- Modal ------------------------------------------------------------------
interface ModalProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  children: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
}

const SIZES = { sm: 'max-w-sm', md: 'max-w-xl', lg: 'max-w-3xl', xl: 'max-w-5xl' }

/** Modals can nest (a lesson opens its payment details). Reference-count the
 *  scroll lock so closing the inner one does not unlock the page underneath. */
let openModals = 0

export function Modal({ open, onClose, title, children, footer, size = 'md' }: ModalProps) {
  useEffect(() => {
    if (!open) return
    openModals += 1
    const depth = openModals

    // Escape must close only the topmost modal. `stopPropagation` cannot do
    // that - every modal listens on `document`, so the event reaches them all
    // regardless. Comparing our depth to the current top is what works: with a
    // payment detail open over a reservation form, Escape closes the detail
    // and leaves the half-filled form alone.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && depth === openModals) onClose()
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      openModals = Math.max(0, openModals - 1)
      if (openModals === 0) document.body.style.overflow = ''
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 p-3 sm:p-6">
      <div
        className={cn('w-full rounded border border-slate-300 bg-white shadow-xl', SIZES[size])}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-3.5 py-2">
          <h2 className="truncate text-[14px] font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="-mr-1 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Zavrieť"
          >
            <X size={16} />
          </button>
        </div>
        <div className="px-3.5 py-3">{children}</div>
        {footer ? (
          <div className="flex justify-end gap-1.5 border-t border-slate-200 bg-slate-50/60 px-3.5 py-2">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  )
}

// --- Form primitives --------------------------------------------------------
export const Input = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    return <input ref={ref} className={cn('input', className)} {...props} />
  },
)

export const Select = forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, children, ...props }, ref) {
    return (
      <select ref={ref} className={cn('input', className)} {...props}>
        {children}
      </select>
    )
  },
)

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, ...props }, ref) {
  return <textarea ref={ref} className={cn('input', className)} rows={3} {...props} />
})

export function Field({
  label,
  error,
  hint,
  children,
  className,
}: {
  label?: ReactNode
  error?: string
  /** Vysvetlivka pod políčkom — čo tá hodnota znamená, keď to z názvu nevidno. */
  hint?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <div className={className}>
      {label ? <label className="label">{label}</label> : null}
      {children}
      {error ? <p className="field-error">{error}</p> : null}
      {/* Chyba vytláča vysvetlivku: keď je políčko zle vyplnené, je dôležitejšie
          povedať čo s tým, než čo to je. */}
      {hint && !error ? <p className="mt-1 text-xs text-slate-500">{hint}</p> : null}
    </div>
  )
}

// `forwardRef` je nutný pre `register()` z react-hook-form: bez neho React 18
// ref zahodí, formulár políčko nevidí a odošle vždy predvolenú hodnotu.
export const Checkbox = forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }
>(({ label, className, ...props }, ref) => (
  <label className={cn('flex cursor-pointer items-center gap-1.5 text-[13px]', className)}>
    <input
      ref={ref}
      type="checkbox"
      className="h-3.5 w-3.5 rounded border-slate-300 text-action-600 focus:ring-action-400"
      {...props}
    />
    <span>{label}</span>
  </label>
))
Checkbox.displayName = 'Checkbox'

// --- Feedback ---------------------------------------------------------------
export function Spinner({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent',
        className,
      )}
      role="status"
      aria-label="Načítavam"
    />
  )
}

export function Alert({
  variant = 'info',
  children,
}: {
  variant?: 'info' | 'error' | 'success' | 'warning'
  children: ReactNode
}) {
  const styles = {
    info: 'bg-blue-50 text-blue-800 border-blue-200',
    error: 'bg-red-50 text-red-800 border-red-200',
    success: 'bg-green-50 text-green-800 border-green-200',
    warning: 'bg-amber-50 text-amber-900 border-amber-200',
  }
  return (
    <div className={cn('rounded border px-2.5 py-1.5 text-[13px]', styles[variant])} role="alert">
      {children}
    </div>
  )
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <div className="px-3 py-6 text-center text-[13px] text-slate-500">{children}</div>
}

export function Badge({
  children,
  color,
  className,
}: {
  children: ReactNode
  color?: string | null
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium',
        !color && 'bg-slate-100 text-slate-700',
        className,
      )}
      style={color ? { backgroundColor: color, color: '#111827' } : undefined}
    >
      {children}
    </span>
  )
}

export function ConfirmButton({
  onConfirm,
  message,
  children,
  className,
}: {
  onConfirm: () => void
  message: string
  children: ReactNode
  className?: string
}) {
  return (
    <button
      type="button"
      className={className}
      onClick={() => {
        if (window.confirm(message)) onConfirm()
      }}
    >
      {children}
    </button>
  )
}

// --- HelpTip ----------------------------------------------------------------
/** Otáznik pri nadpise - vysvetlenie sa ukáže až po kliknutí, ako bublina.
 *  Zatvorí sa ďalším kliknutím na otáznik alebo kliknutím kamkoľvek inam. */
export function HelpTip({
  label,
  children,
  icon,
  align = 'left',
}: {
  label: string
  children: ReactNode
  /** Namiesto otáznika (napr. zámok pri platbe s dokladom). */
  icon?: ReactNode
  /** Pri pravom okraji okna bublina rastie doľava. */
  align?: 'left' | 'right'
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  return (
    <span ref={ref} className="relative inline-flex align-middle">
      <button
        type="button"
        className={cn(
          'rounded-full p-0.5 text-slate-400 hover:text-brand-600',
          open && 'text-brand-600',
        )}
        aria-label={label}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {icon ?? <CircleHelp size={15} />}
      </button>
      {open ? (
        <span
          role="tooltip"
          className={cn(
            'absolute top-full z-30 mt-1 w-72 rounded-md border border-slate-200 bg-white p-2.5 text-left text-[12px] font-normal normal-case leading-snug text-slate-600 shadow-lg',
            align === 'right' ? 'right-0' : 'left-0',
          )}
        >
          {children}
        </span>
      ) : null}
    </span>
  )
}
